import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FleetTrackerIconService } from './fleet-tracker-icon.service';
import { FleetTrackerIcons } from './fleet-tracker-icons';
import {
    FleetTrackerUtilityService,
    NativeFenceLayer,
    NativeFenceMarker,
    NativeHistoryTraceLayer,
    NativeMarkerLayer,
    // NativeTraceLayer,
    ShapeWithBorder
} from './fleet-tracker-utility.service';
import { MarkerwithLabel } from './model/marker-label.model';
import {
    DirectionalMarkerModel,
    FenceLayerModel,
    FenceModel,
    IconType,
    LayerIndex,
    MarkerLayerModel,
    MarkerModel,
    PointModel,
    ShapeType,
    ZoomIndex
} from './model/models';

declare var MarkerClusterer: any;

const MIN_CLUSTER_MARKERS = 5;
const MAX_CLUSTER_ZOOM_LEVEL = 18;
export const MARKER_LABEL_DEFAULT_FONT_SIZE = '12px';

@Injectable()
export class FleetTrackerService {

    private _onMarkerClick = new Subject<google.maps.Marker>();
    // private _onHistoryMarkerClick = new Subject<google.maps.Marker>();
    private _onBorderClick = new Subject<google.maps.Polyline | google.maps.Marker>();
    private _onLeave = new Subject<google.maps.Marker>();
    private _onEnter = new Subject<google.maps.Marker>();
    private markerLayer: MarkerLayerModel<any>;
    private layerIndex: LayerIndex;
    private markerLayerAdded: boolean;

    onMarkerClick: Observable<google.maps.Marker> = this._onMarkerClick.asObservable();
    // onHistoryMarkerClick: Observable<google.maps.Marker> = this._onHistoryMarkerClick.asObservable();
    onBorderClick: Observable<google.maps.Polyline | google.maps.Marker> = this._onBorderClick.asObservable();
    onLeave: Observable<google.maps.Marker> = this._onLeave.asObservable();
    onEnter: Observable<google.maps.Marker> = this._onEnter.asObservable();

    private _onMapZoom = new Subject<number>();
    private _onMapClick = new Subject<google.maps.LatLng>();

    onMapZoom: Observable<number> = this._onMapZoom.asObservable();
    onMapClick: Observable<google.maps.LatLng> = this._onMapClick.asObservable();

    private _icons: Map<IconType, google.maps.Icon[][]>;
    // private _traces = new Map<LayerIndex, NativeTraceLayer<any>>();
    private _fences = new Map<LayerIndex, NativeFenceLayer<any>>();
    private _fenceMarkers = new Map<LayerIndex, NativeFenceMarker<any>>();
    private _markers = new Map<LayerIndex, NativeMarkerLayer<any>>();
    // private _liveMarkers = new Map<LayerIndex, NativeMarkerLayerLive<any>>();
    // private _addresses = new Map<LayerIndex, NativeAddressLayer<any>>();
    private _callOutMarker: google.maps.Marker;
    private _historyTraces = new Map<LayerIndex, NativeHistoryTraceLayer<any>>();

    private _googleMap: google.maps.Map;

    // geofence shape on click
    private fenceCircleOnClick: google.maps.Circle;
    private fencePolygonOnClick: google.maps.Polygon;
    private fencePolylineOnClick: google.maps.Polyline;

    // geofence shape on hover
    private fenceCircleOnHover: google.maps.Circle;
    private fencePolygonOnHover: google.maps.Polygon;
    private fencePolylineOnHover: google.maps.Polyline;

    private readonly _exclusiveBorderPattern = [{
        icon: { path: 'M 0,0 0,0.2', strokeOpacity: 0.75, scale: 2 },
        repeat: '8px',
        offset: '0'
    }];
    private readonly _inclusiveBorderPattern = [{
        icon: { path: 'M 0,0 0,0.2', strokeOpacity: 0.75, scale: 2 },
        repeat: '1px',
        offset: '0'
    }];
    _markerCluster = new Map<LayerIndex, any>();

    private OPACITY_LEVEL = 0.35;
    private TOP_Z_INDEX = 10;
    private _padding: google.maps.Padding = {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
    };

    constructor(
        private _zone: NgZone,
        private _iconService: FleetTrackerIconService,
        private _utility: FleetTrackerUtilityService
    ) {
        this._icons = _iconService.icons;
    }

    //
    // Set map instance and add click and zoom listeners.
    //
    set googleMap(googleMap: google.maps.Map) {
        this._googleMap = googleMap;
        this._googleMap.addListener('click', (event: google.maps.MapMouseEvent | google.maps.IconMouseEvent) => {
            if (FleetTrackerService.isIconMouseEvent(event)) {
                const panPadding = {
                    ...this._padding
                };
                // Half the width of the POI info window
                panPadding.left += 105;
                panPadding.right += 105;
                this.googleMap.panToBounds(new google.maps.LatLngBounds(event.latLng), panPadding);
            }
            this._zone.run(() => {
                this._onMapClick.next(event.latLng);
                this.removeFenceOnClickAndHover(true);
            });
        });
        this._googleMap.addListener('zoom_changed', () => {
            const zoom = this._googleMap.getZoom();
            this._utility.showZoomDependentLayers(zoom, this._markers);
            this._zone.run(() => this._onMapZoom.next(zoom));
        });

        const latLng: google.maps.LatLngLiteral = { lat: 37.7749, lng: -122.4194 };

        this._iconService.initialize();
    }

    get googleMap(): google.maps.Map {
        return this._googleMap;
    }

    addMarkerLayer<T>(layer: MarkerLayerModel<T>, index: LayerIndex) {
        this.markerLayer = layer;
        if (layer) {
            this.markerLayer = layer;
            this.layerIndex = index;
            this.addNativeMarkerLayer(layer.layer, layer.zoom, layer.markers, layer.cluster);
        }
    }

    addFenceLayer<T>(layer: FenceLayerModel<T>) {
        if (layer) {
            this.addNativeFenceLayer(layer.layer, layer.fences, layer.showMarker);
        }
    }

    addFenceMarker<T>(layer: FenceLayerModel<T>) {
        if (layer) {
            this.addNativeFenceMarker(layer.layer, layer.fences, layer.showMarker);
        }
    }

    removeFenceLayer(layer: LayerIndex) {
        this.removeFenceOnClickAndHover(true); // Remove geofence layer.
        if (this._fences.get(layer)) {
            this._fences.get(layer).nativePolygons.forEach(t => t.setMap(undefined));
            this._fences.get(layer).fences = []
            this._fences.get(layer).nativePolylines?.forEach(t => t.setMap(undefined));
            this._fences.delete(layer);
        }
    }

    //
    // Hide or show the trace.
    //
    showFenceLayer(layer: LayerIndex, state: boolean) {
        if (this._fences.get(layer)) {
            // this._fences.get(layer).nativeCircles.forEach(t => t.setVisible(state));
            this._fences.get(layer).nativePolygons.forEach(t => t.setVisible(state));
        }
    }

    //
    // Zap the native map property of each marker in the given layer and set layer to empty.
    //
    removeMarkerLayer(layer: LayerIndex) {
        if (this._markers.get(layer)) {
            this._markers.get(layer).nativeMarkers.forEach(m => m.setMap(undefined));
            this._markers.delete(layer);
            this.markerLayerAdded = false;
        }
    }

    // removeMarkerLayerLive(layer: LayerIndex) {
    //     if (this._liveMarkers.get(layer)) {
    //         this._liveMarkers.get(layer).nativeMarkers.forEach(m => m.setMap(undefined));
    //         this._liveMarkers.delete(layer);
    //     }
    // }

    // removeAddressLayer(layer: LayerIndex) {
    //     if (this._addresses.get(layer)) {
    //         this._addresses.get(layer).nativeMarkers.forEach(m => m.setMap(undefined));
    //         this._addresses.delete(layer);
    //     }
    // }

    //
    // Set the visibility state of the layer and each marker in the layer.
    //
    showMarkerLayer(layer: LayerIndex, state: boolean) {
        if (this._markers.get(layer)) {
            this._markers.forEach(layer => layer.visible = state);
            this._markers.get(layer).nativeMarkers.forEach(m => m.setVisible(state));
        }
    }

    // showMarkerLayerLive(layer: LayerIndex, state: boolean) {
    //     if (this._liveMarkers.get(layer)) {
    //         this._liveMarkers.forEach(layer => layer.visible = state);
    //         this._liveMarkers.get(layer).nativeMarkers.forEach(m => m.setVisible(state));
    //     }
    // }

    // showAddressLayer(layer: LayerIndex, state: boolean) {
    //     if (this._addresses.get(layer)) {
    //         this._addresses.forEach(layer => layer.visible = state);
    //         this._addresses.get(layer).nativeMarkers.forEach(m => m.setVisible(state));
    //     }
    // }

    // highlightTrails(id: string) {
    //     const trailLayer = this.getMarkerLayer(LayerIndex.Trail);

    //     if (trailLayer) {
    //         const markers = trailLayer.markers;
    //         const nativeMarkers = trailLayer.nativeMarkers;
    //         for (let i = 0; i < markers.length; i++) {
    //             if (markers[i].id === id) {
    //                 nativeMarkers[i].setZIndex(this.TOP_Z_INDEX);
    //             } else {
    //                 nativeMarkers[i].setOpacity(this.OPACITY_LEVEL);
    //             }
    //         }
    //     }

    //     // hide traces
    //     const tracesLayer = this.getTraceLayer();

    //     if (tracesLayer) {
    //         const traces = tracesLayer.traces;
    //         const nativeTraces = tracesLayer.nativeTraces;
    //         for (let i = 0; i < traces.length; i++) {
    //             const markers = traces[i].markers;
    //             for (let j = 0; j < markers.length; j++) {
    //                 if (markers[j].id !== id) {
    //                     nativeTraces[i].setOptions({ 'strokeOpacity': this.OPACITY_LEVEL });
    //                 }
    //             }
    //         }
    //     }
    // }

    // resetTrailsOpacity() {
    //     // set all trails to opaque
    //     // set all trails z-index to default
    //     const trailLayer = this.getMarkerLayer(LayerIndex.Trail);
    //     if (trailLayer) {
    //         const markers = trailLayer.nativeMarkers;
    //         markers.forEach(marker => {
    //             marker.setOpacity(1);
    //             marker.setZIndex(LayerIndex.Trail);
    //         });
    //     }
    //     // set traces back to opaque
    //     const tracesLayer = this.getTraceLayer();
    //     if (tracesLayer) {
    //         const traces = tracesLayer.nativeTraces;
    //         traces.forEach(trace => {
    //             trace.setOptions({ 'strokeOpacity': 1 });
    //         });
    //     }
    // }

    getMarkerLayer(layerIndex: LayerIndex) {
        return this._markers.get(layerIndex);
    }

    // getTraceLayer() {
    //     return this._traces.get(LayerIndex.Trace);
    // }

    //
    // Hide or show the trace.
    //
    // showTraceLayer(layer: LayerIndex, state: boolean) {
    //     if (this._traces.get(layer)) {
    //         this._traces.get(layer).nativeTraces.forEach(t => t.setVisible(state));
    //     }
    // }


    //
    // Hide or show the history trace.
    //
    showHistoryLayer(layer: LayerIndex, state: boolean) {
        if (this._historyTraces.get(layer)) {
            this._historyTraces.get(layer).nativeTraces.forEach(t => t.setVisible(state));
        }
    }

    //
    // Detach the map instance from the path and remove the path from the path dictionary.
    //
    // removeTraceLayer(layer: LayerIndex) {
    //     if (this._traces.get(layer)) {
    //         this._traces.get(layer).nativeTraces.forEach(t => t.setMap(undefined));
    //         this._traces.delete(layer);
    //     }
    // }

    //
    // Detach the map instance from the path and remove the history traces from the path dictionary.
    //
    removeHistoryLayer(layer: LayerIndex) {
        if (this._historyTraces.get(layer)) {
            this._historyTraces.get(layer).nativeTraces.forEach(t => t.setMap(undefined));
            this._historyTraces.get(layer).nativeMarkers.forEach(t => t.forEach(x => x.marker.setMap(undefined)));
            this._historyTraces.get(layer).nativeMarkers.forEach(t => {
                t.forEach(x => {
                    if (x.label) {
                        x.label.setMap(undefined);
                    }
                });
            });
            this._historyTraces.delete(layer);
        }
    }

    //
    // Pan to the center and zoom to the box inscribed by the circle determined by the radius.
    //
    setRadius(radius: number, padding: google.maps.Padding) {
        const center = this._googleMap.getCenter();
        const circle = new google.maps.Circle({ radius: radius, center: { lat: center.lat(), lng: center.lng() } });
        this._googleMap.fitBounds(circle.getBounds(), padding);
    }

    //
    // Zoom to include all of the given points in the view.
    //
    setBounds(points: PointModel[], padding: google.maps.Padding) {
        const bounds = new google.maps.LatLngBounds();
        points.forEach(point => bounds.extend(point));
        this._googleMap.fitBounds(bounds, padding);
    }

    getMarkerIcon<T>(marker: MarkerModel<T>): google.maps.Icon {
        return this._utility.getMarkerIcon(marker, this._icons);
    }

    // =================================== Internals ================================

    //
    // Generate native markers assigned to a particular z-index layer and zoom-sensitivity.
    //
    private addNativeMarkerLayer<T>(layer: LayerIndex, zoom: ZoomIndex, markers: MarkerModel<T>[], cluster: boolean) {
        const nativeMarkers: google.maps.Marker[] = [];

        markers.map((marker, z) => nativeMarkers.push(this.createNativeMarker(layer, marker, z)));

        this._markers.set(layer, {
            layer: layer,
            zoom: zoom,
            markers: markers,
            nativeMarkers: nativeMarkers,
            visible: false,
            cluster: cluster
        });

        if (cluster) {
            this.clusterize(nativeMarkers, this._googleMap, layer);
        }
    }

    // private addNativeAddressLayer<T>(layer: LayerIndex, zoom: ZoomIndex, addresses: MarkerModel<T>[]) {
    //     const nativeMarkers: google.maps.Marker[] = [];

    //     addresses.map((address, z) => {
    //         nativeMarkers.push(this.createNativeMarker(layer, address, z));
    //     });

    //     this._addresses.set(layer, {
    //         layer: layer, zoom: zoom, addresses: addresses, nativeMarkers: nativeMarkers, visible: false
    //     });

    // }

    // private addNativeMarkerLayerLive<T>(layer: LayerIndex, zoom: ZoomIndex, markers: DirectionalMarkerModel<T>[], cluster: boolean, showLabel: boolean) {
    //     this.processMarkerFontSize(markers);

    //     const markerStore = this._liveMarkers.get(layer) ? this._liveMarkers.get(layer).nativeMarkers : new Map<string, google.maps.Marker>();
    //     if (markers.length === 0) {
    //         this.clearClusterMarkers(layer);
    //         this.removeMarkerLayerLive(layer);
    //         markerStore.clear();
    //     }
    //     markers.map((marker, z) => {
    //         const coords = new google.maps.LatLng(marker.lat, marker.lng);
    //         switch (marker.state) {
    //             case MarkerState.Delete:
    //                 if (markerStore.get(marker.id)) {
    //                     this.deleteMarker(markerStore, marker);
    //                 }
    //                 break;
    //             case MarkerState.Update:
    //             case MarkerState.Add:
    //             default:
    //                 if (markerStore.get(marker.id)) {
    //                     this.updateMarker(marker, markerStore, coords, markers.length, showLabel);
    //                 } else {
    //                     markerStore.set(marker.id, this.addMarker(marker, coords, z, markers.length, showLabel));
    //                 }
    //                 break;
    //         }
    //     });
    //     const nativeMarkers: google.maps.Marker[] = Array.from(markerStore.values());
    //     this._liveMarkers.set(layer, {
    //         layer: layer,
    //         zoom: zoom,
    //         markers: markers,
    //         nativeMarkers: markerStore,
    //         visible: false,
    //         cluster: cluster
    //     });
    //     this.removeClustering(layer);
    //     if (cluster) {
    //         this.clusterize(nativeMarkers, this._googleMap, layer);
    //     }
    // }

    private addMarker<T>(marker: DirectionalMarkerModel<T>, coords: google.maps.LatLng, layer: number, totalMarkers: number, showLabel: boolean) {
        const nativeMarker = new google.maps.Marker({
            position: coords,
            visible: false,
            zIndex: layer + 100,
            map: this._googleMap
        });
        nativeMarker.set('id', marker.id);
        // TODO: emit MarkerModel<T> once we stop depending on google.maps.InfowWindow in apps.
        nativeMarker.set('attachment', marker); // attach marker model
        if (totalMarkers <= 500) {
            nativeMarker.setIcon(this.getMarkerIcon(marker));
            if (showLabel) {
                this.setMarkerLabel(nativeMarker, marker.fontSize, marker.label);
            } else {
                this.setMarkerLabel(nativeMarker, marker.fontSize);
            }
        } else {
            nativeMarker.setIcon(this._utility.getMarkerIconLive(marker));
            if (showLabel) {
                this.addMouseAndTouchListenersLive(nativeMarker, marker);
            }
        }
        nativeMarker.addListener('click', () => this.markerClick(nativeMarker));

        return nativeMarker;
    }

    private updateMarker<T>(marker: DirectionalMarkerModel<T>, markerStore: Map<string, google.maps.Marker>, coords: google.maps.LatLng, totalMarkers: number, showLabel: boolean) {
        const updatedMarker = markerStore.get(marker.id);
        updatedMarker.setPosition(coords);
        updatedMarker.set('attachment', marker);
        if (totalMarkers <= 500) {
            updatedMarker.setIcon(this.getMarkerIcon(marker));
            if (showLabel) {
                this.setMarkerLabel(updatedMarker, marker.fontSize, marker.label);
            } else {
                this.setMarkerLabel(updatedMarker, marker.fontSize);
            }
            this.clearMouseAndTouchListenersLive(updatedMarker);
        } else {
            this.setMarkerLabel(updatedMarker, marker.fontSize);
            updatedMarker.setIcon(this._utility.getMarkerIconLive(marker));
            if (showLabel) {
                this.addMouseAndTouchListenersLive(updatedMarker, marker);
            } else {
                this.clearMouseAndTouchListenersLive(updatedMarker);
            }
        }
    }

    private deleteMarker<T>(markerStore: Map<string, google.maps.Marker>, marker: DirectionalMarkerModel<T>) {
        markerStore.get(marker.id).setMap(undefined);
        markerStore.delete(marker.id);
    }

    // private addNativeTraceLayer<T>(layer: LayerIndex, zoom: ZoomIndex, traces: TraceModel<T>[]) {
    //     const nativeTraces: google.maps.Polyline[] = [];

    //     traces.forEach(trace => {
    //         nativeTraces.push(this.createNativePolyline(layer, trace.markers, trace.color, trace.opacity, trace.weight));
    //     });
    //     this._traces.set(layer, { layer: layer, zoom: zoom, traces: traces, nativeTraces: nativeTraces });
    // }

    // private addNativeHistoryLayer<T>(layer: LayerIndex, zoom: ZoomIndex, traces: TraceModel<T>[], showLabel: boolean, showHistoryTrackpoints: boolean) {
    //     const historyTraces: google.maps.Polyline[] = [];
    //     const nativeMarkers = new Map<string, MarkerwithLabel[]>();

    //     traces.forEach((trace, zIndex) => {
    //         if (trace.markers.length > 0) {
    //             const history = this.addHistoryPolyline(layer, trace, nativeMarkers, showLabel, zIndex, showHistoryTrackpoints);
    //             historyTraces.push(history.trace);
    //             nativeMarkers.set(trace.markers[0].id, history.nativeMarkersWithLabel);
    //         }
    //     });
    //     this._historyTraces.set(layer, {
    //         layer: layer,
    //         zoom: zoom,
    //         traces: traces,
    //         nativeTraces: historyTraces,
    //         nativeMarkers: nativeMarkers
    //     });
    // }

    private createNativePolyline<T>(layer: LayerIndex, markers: MarkerModel<T>[], color: string, opacity: number, weight: number): google.maps.Polyline {

        const points = markers.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{ lat: m.lat, lng: m.lng });

        const trace = new google.maps.Polyline({
            path: points,
            geodesic: true,
            clickable: true,
            strokeColor: color,
            strokeOpacity: opacity,
            strokeWeight: weight,
            map: this._googleMap,
            zIndex: layer,
            visible: false,
        });

        trace.addListener('click', (event: google.maps.MapMouseEvent) => {
            trace.set('selectedPoint', event.latLng);
            this.borderClick(trace);
        });

        return trace;
    }

    //
    // Add a path connecting the given points.
    //
    // private addHistoryPolyline<T>(layer: LayerIndex, traceModel: TraceModel<T>, nativeMarkers: Map<string, MarkerwithLabel[]>, showLabel: boolean, zIndex: number, showHistoryTrackpoints: boolean): { trace: google.maps.Polyline, nativeMarkersWithLabel: MarkerwithLabel[] } {
    //     const markers = traceModel.markers;
    //     this.processMarkerFontSize(markers);

    //     const color = traceModel.color;
    //     const opacity = traceModel.opacity;
    //     const weight = traceModel.weight;
    //     const points = markers.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{ lat: m.lat, lng: m.lng });

    //     const trace = new google.maps.Polyline({
    //         path: points,
    //         geodesic: false,
    //         strokeColor: color,
    //         strokeOpacity: opacity,
    //         strokeWeight: weight,
    //         map: this._googleMap,
    //         zIndex: layer,
    //         visible: false
    //     });
    //     let nativeMarkersWithLabel: MarkerwithLabel[] = [];
    //     let nativeMarkerWithLabel: MarkerwithLabel;
    //     if (markers.length > 0 && showHistoryTrackpoints) { // To show all History Trackpoints
    //         markers.forEach((marker) => {
    //             const nativeMarker = new google.maps.Marker({
    //                 position: { lat: marker.lat, lng: marker.lng },
    //                 visible: true,
    //                 zIndex: layer + zIndex,
    //                 icon: this.getMarkerIcon(marker),
    //                 map: this._googleMap
    //             });
    //             const labelMarker = showLabel ? this.addLabel(nativeMarker, marker, layer + zIndex) : undefined;
    //             labelMarker ? labelMarker.setVisible(false) : null;
    //             nativeMarker.set('attachment', marker); // attach marker model
    //             nativeMarker.addListener('click', () => this.historyMarkerClick(nativeMarker));
    //             nativeMarkerWithLabel = { marker: nativeMarker, label: labelMarker };
    //             nativeMarker.addListener('mouseover', (event) => {
    //                 if (showLabel) {
    //                     labelMarker.setVisible(true);
    //                 }
    //             });
    //             nativeMarker.addListener('mouseout', (event) => {
    //                 if (showLabel) {
    //                     labelMarker.setVisible(false);
    //                 }
    //             });
    //             nativeMarkersWithLabel.push(nativeMarkerWithLabel);
    //         });
    //     } else if (markers.length > 0 && !showHistoryTrackpoints) { // To show History Trackpoints when traces are hovered
    //         nativeMarkerWithLabel = nativeMarkers.get(markers[0].id) && nativeMarkers.get(markers[0].id)[0];
    //         if (!nativeMarkerWithLabel) {
    //             const nativeMarker = new google.maps.Marker({
    //                 position: points.values().next().value,
    //                 visible: true,
    //                 zIndex: layer + zIndex,
    //                 icon: this.getMarkerIcon(markers[0]),
    //                 map: this._googleMap
    //             });
    //             const labelMarker = showLabel ? this.addLabel(nativeMarker, markers[0], layer + zIndex) : undefined;
    //             nativeMarkerWithLabel = { marker: nativeMarker, label: labelMarker };
    //         } else {
    //             const nativeMarker = nativeMarkerWithLabel.marker;
    //             nativeMarker.setPosition(points.values().next().value);
    //             nativeMarker.setVisible(true);
    //             nativeMarker.set('zIndex', layer);
    //             nativeMarker.setMap(this._googleMap);
    //             nativeMarker.setIcon(this.getMarkerIcon(markers[0]));
    //         }
    //         nativeMarkerWithLabel.marker.set('attachment', markers[0]); // attach marker model

    //         trace.addListener('mouseover', (event) => this.hoverOnTrace(nativeMarkerWithLabel, event));
    //         trace.addListener('mousemove', (event) => this.mouseMoveOnTrace(markers, nativeMarkerWithLabel, event));
    //         trace.addListener('mousedown', (event) => this.mouseMoveOnTrace(markers, nativeMarkerWithLabel, event));
    //         nativeMarkerWithLabel.marker.addListener('click', () => this.historyMarkerClick(nativeMarkerWithLabel.marker));
    //         nativeMarkersWithLabel.push(nativeMarkerWithLabel);
    //     }
    //     return { trace, nativeMarkersWithLabel };
    // }

    private addNativeFenceLayer<T>(layer: LayerIndex, fences: FenceModel<T>[], showMarker: boolean) {
        const nativePolygons: google.maps.Polygon[] = [];

        fences.forEach(fence => {
            this._utility.setFenceDefaults(fence); // assign default values to optional properties
            const shape = this.createNativePolygon(layer, fence);
            nativePolygons.push(<google.maps.Polygon>shape.shape);
        });

        this._fences.set(layer, {
            layer: layer,
            fences: fences,
            showMarker: showMarker,
            nativePolygons: nativePolygons
        });
    }

    // add's Geofence marker.
    // show Geofence shape when clicked/hovered on the Geofence marker.
    // hide Geofence shape when clicked/hovered outside the Geofence marker.
    private addNativeFenceMarker<T>(layer: LayerIndex, fences: FenceModel<T>[], showMarker: boolean) {
        const nativeMarkers: google.maps.Marker[] = [];
        let visible = this.markerLayer ? true : false; // a layer can be undefined

        if (visible && this.googleMap.getZoom() < this.markerLayer.zoom) {
            visible = false; // not visible at this zoom level
        }

        fences.forEach(fence => {
            this._utility.setFenceDefaults(fence);
            if (fence.shape === ShapeType.circle) {
                const center = fence.points.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{
                    lat: m.lat,
                    lng: m.lng
                })[0];
                const fenceMarker = this.getFenceMarker(layer, fence, center);
                fenceMarker.addListener('click', () => {
                    // this.addMarkerLayer(this.markerLayer, this.layerIndex);
                    // this.showMarkerLayer(this.layerIndex, visible);
                    this.borderClick(fenceMarker);
                    this.removeFenceOnClickAndHover(false);
                    const shape = this.createNativeCircle(layer, fence, true);
                    this.fenceCircleOnClick = <google.maps.Circle>shape.shape;
                    this.fencePolylineOnClick = shape.border;
                });
                fenceMarker.addListener('mouseover', () => {
                    this.removeFenceOnClickAndHover(false);
                    const shape = this.createNativeCircle(layer, fence, true);
                    this.fenceCircleOnHover = <google.maps.Circle>shape.shape;
                    this.fencePolylineOnHover = shape.border;
                });
                fenceMarker.addListener('mouseout', () => {
                    this.removeFenceOnClickAndHover(false);
                });
                nativeMarkers.push(fenceMarker);
            } else {
                const nativePoints = fence.points.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{
                    lat: m.lat,
                    lng: m.lng
                });

                const bounds = new google.maps.LatLngBounds();
                for (let i = 0; i < nativePoints.length; i++) {
                    bounds.extend(nativePoints[i]);
                }
                const center = bounds.getCenter().toJSON(); // find's and return center latlong  point
                const fenceMarker = this.getFenceMarker(layer, fence, center);
                fenceMarker.addListener('click', () => {
                    // visible = this.geofenceDisplayed? false:true;
                    // if(!this.markerLayerAdded) {
                    //   this.addMarkerLayer(this.markerLayer, this.layerIndex);
                    //   this.markerLayerAdded = true;
                    // }
                    // this.showMarkerLayer(this.layerIndex, visible);
                    this.borderClick(fenceMarker);
                    this.removeFenceOnClickAndHover(true);
                    const shape = this.createNativePolygon(layer, fence, true);
                    this.fencePolygonOnClick = <google.maps.Polygon>shape.shape;
                    if (shape.border) {
                        this.fencePolylineOnClick = shape.border;
                    }
                });
                fenceMarker.addListener('mouseover', () => {
                    this.removeFenceOnClickAndHover(false);
                    const shape = this.createNativePolygon(layer, fence, true);
                    this.fencePolygonOnHover = <google.maps.Polygon>shape.shape;
                    if (shape.border) {
                        this.fencePolylineOnHover = shape.border;
                    }
                });
                fenceMarker.addListener('mouseout', () => {
                    this.removeFenceOnClickAndHover(false);
                });
                nativeMarkers.push(fenceMarker);
            }

            this._fenceMarkers.set(layer, {
                layer: layer,
                fences: fences,
                nativeMarkers: nativeMarkers,
                showMarker: showMarker
            });
        });
    }

    // Remove fence shape based on clicked/hovered
    removeFenceOnClickAndHover(clearAll: boolean) {
        if (clearAll) {
            this.fenceCircleOnClick?.setMap(undefined);
            this.fencePolygonOnClick?.setMap(undefined);
            this.fencePolylineOnClick?.setMap(undefined);
            this.fenceCircleOnHover?.setMap(undefined);
            this.fencePolygonOnHover?.setMap(undefined);
            this.fencePolylineOnHover?.setMap(undefined);
        } else {
            this.fenceCircleOnHover?.setMap(undefined);
            this.fencePolygonOnHover?.setMap(undefined);
            this.fencePolylineOnHover?.setMap(undefined);
        }
    }

    //
    // Create a path connecting the given points.
    //
    private createNativePolygon<T>(layer: LayerIndex, fence: FenceModel<T>, visible = false): ShapeWithBorder {

        if (fence.points.length < 3) {
            throw 'A polygonal geofence needs at least three points';
        }

        const nativePoints = fence.points.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{
            lat: m.lat,
            lng: m.lng
        });

        const polygon = new google.maps.Polygon({
            paths: nativePoints,
            clickable: false,
            strokeColor: fence.penColor,
            strokeOpacity: fence.exclusive ? 0 : fence.penWeight, // hide border on exclusive fences
            strokeWeight: fence.penWeight,
            fillColor: fence.brushColor,
            fillOpacity: fence.brushOpacity,
            map: this._googleMap,
            zIndex: layer,
            visible: visible
        });

        return { shape: polygon };
    }

    //
    // Add a circle centered about a point with the given radius.
    //
    private createNativeCircle<T>(layer: LayerIndex, fence: FenceModel<T>, visible = false): ShapeWithBorder {

        if (!fence.radius) {
            throw 'A circular geofence must provide a radius';
        }

        if (fence.points.length !== 1) {
            throw 'A circular geofence has a single point: the center';
        }

        const center = fence.points.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{
            lat: m.lat,
            lng: m.lng
        })[0];

        const circle = new google.maps.Circle({
            center: center,
            clickable: false,
            strokeOpacity: 0,
            fillColor: fence.brushColor,
            fillOpacity: fence.brushOpacity,
            map: this._googleMap,
            zIndex: layer,
            visible: visible,
            radius: fence.radius
        });

        const polylineOptions: google.maps.PolylineOptions = {
            clickable: true,
            strokeColor: fence.penColor,
            strokeWeight: fence.penWeight < 1 ? fence.penWeight : 10,
            strokeOpacity: 0,
            path: this._utility.generateCircle(fence.radius, center),
            icons: fence.exclusive ? this._exclusiveBorderPattern : this._inclusiveBorderPattern,
            map: this._googleMap
        };

        const border = new google.maps.Polyline(polylineOptions);
        border.set('attachment', fence); // attach marker model
        border.addListener('click', () => this.borderClick(border));
        return { shape: circle, border: border };
    }

    // returns Geofence marker of type <google.maps.Marker>
    private getFenceMarker<T>(layer: LayerIndex, fence: FenceModel<T>, center: google.maps.LatLngLiteral): google.maps.Marker {
        const marker: MarkerModel<T> = fence.markerModel;
        marker.lat = center.lat;
        marker.lng = center.lng;

        const nativeMarker = new google.maps.Marker({
            position: center,
            visible: true,
            zIndex: 0,
            icon: this.getMarkerIcon(marker),
            map: this._googleMap
        });
        nativeMarker.set('attachment', fence);
        if (marker.icon === IconType.GeofenceIcon) {
            this.setMarkerLabel(nativeMarker, marker.fontSize, marker.label);
        }
        return nativeMarker;
    }

    //
    // Add a shaded rectangle with an inner circular polygon (creates a spotlight effect).
    //
    private createNativeSpotlight<T>(layer: LayerIndex, fence: FenceModel<T>): ShapeWithBorder {

        if (!fence.radius) {
            throw 'A circular geofence must provide a radius';
        }

        if (fence.points.length !== 1) {
            throw 'A circular geofence has a single point: the center';
        }

        const center = fence.points.map<google.maps.LatLngLiteral>(m => <google.maps.LatLngLiteral>{
            lat: m.lat,
            lng: m.lng
        })[0];

        const circle = this._utility.generateCircle(fence.radius, center); // spotlight

        const eclipse = [
            { lat: 85, lng: 180 },
            { lat: 85, lng: 0 },
            { lat: 85, lng: -180 },
            { lat: 0, lng: -180 },
            { lat: -85, lng: -180 },
            { lat: -85, lng: 0 },
            { lat: -85, lng: 180 },
            { lat: 0, lng: 180 },
            { lat: 85, lng: 180 }
        ];

        const shape = new google.maps.Polygon({
            clickable: false,
            strokeColor: fence.penColor,
            strokeWeight: fence.penWeight,
            fillOpacity: fence.brushOpacity,
            fillColor: fence.brushColor,
            zIndex: layer,
            paths: [eclipse, circle],
            map: this._googleMap
        });

        return { shape: shape, border: undefined };
    }

    private addLabel(nativeMarker: google.maps.Marker, marker: any, layer: number) {
        const image = {
            url: this._iconService.getCallOutIcon,
            anchor: new google.maps.Point(51, -10),
            origin: new google.maps.Point(0, 1)
        };
        return new google.maps.Marker({
            position: nativeMarker.getPosition(),
            visible: true,
            label: { color: 'white', fontSize: marker.fontSize, fontFamily: 'Lato', fontWeight: 'normal', text: marker.label },
            zIndex: layer + 100,
            icon: image,
            map: this._googleMap
        });
    }

    private markerClick<T>(marker: google.maps.Marker) { // update current pixel coordinates
        const attachment = <MarkerModel<T>>marker.get('attachment');
        attachment.pixel = this._utility.positionToPixel(marker.getPosition(), this._googleMap);
        this._zone.run(() => this._onMarkerClick.next(marker));
    }

    private borderClick<T>(border: google.maps.Polyline | google.maps.Marker) {
        this._zone.run(() => this._onBorderClick.next(border));
    }

    private mouseEnter<T>(nativeMarker: google.maps.Marker, marker: DirectionalMarkerModel<T>) {
        const image = {
            url: this._iconService.getCallOutIcon,
            anchor: new google.maps.Point(51, 0),
            origin: new google.maps.Point(0, 1)
        };
        const label = {
            color: 'white',
            fontSize: marker.fontSize,
            fontFamily: 'Lato',
            fontWeight: 'normal',
            text: marker.label
        };

        if (!this._callOutMarker) {
            const callOutMarker = new google.maps.Marker({
                position: nativeMarker.getPosition(),
                visible: true,
                label: label,
                zIndex: 1000,
                icon: image,
                map: this._googleMap
            });
            this._callOutMarker = callOutMarker;
        } else {
            this._callOutMarker.setPosition(nativeMarker.getPosition());
            this._callOutMarker.setLabel(label);
            this._callOutMarker.setMap(this._googleMap);
        }
        this._zone.run(() => this._onEnter.next(nativeMarker));
    }

    private mouseLeave<T>(marker: google.maps.Marker) {
        if (this._callOutMarker) {
            this._callOutMarker.setMap(undefined);
        }
        this._zone.run(() => this._onLeave.next(marker));
    }

    private hoverOnTrace<T>(nativeMarkerWithLabel: MarkerwithLabel, event: any) {
        nativeMarkerWithLabel.marker.setPosition(event.latLng);
        nativeMarkerWithLabel.marker.setVisible(true);
    }

    private mouseMoveOnTrace<T>(markers: MarkerModel<T>[], nativeMarkerWithLabel: MarkerwithLabel, event: any) {
        let closestMarker;
        markers.reduce(function (carry, marker) {
            const d = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(marker.lat, marker.lng), event.latLng);
            if (d < carry || carry === -1) {
                closestMarker = marker;
                return d;
            }
            return carry;
        }, -1);
        nativeMarkerWithLabel.marker.setPosition(new google.maps.LatLng(closestMarker.lat, closestMarker.lng));
        if (nativeMarkerWithLabel.label) {
            nativeMarkerWithLabel.label.setPosition(new google.maps.LatLng(closestMarker.lat, closestMarker.lng));
        }
        nativeMarkerWithLabel.marker.setIcon(this.getMarkerIcon(closestMarker));
        nativeMarkerWithLabel.marker.set('attachment', closestMarker);
        nativeMarkerWithLabel.marker.setVisible(true);
    }

    // private historyMarkerClick<T>(marker: google.maps.Marker) {
    //     this._zone.run(() => this._onHistoryMarkerClick.next(marker));
    // }

    private addMouseAndTouchListenersLive<T>(nativeMarker: google.maps.Marker, marker: DirectionalMarkerModel<T>) {
        nativeMarker.addListener('mouseover', () => this.mouseEnter(nativeMarker, marker));
        nativeMarker.addListener('mouseout', () => this.mouseLeave(nativeMarker));
        nativeMarker.addListener('mousedown', () => this.mouseEnter(nativeMarker, marker));
        nativeMarker.addListener('mouseup', () => this.mouseLeave(nativeMarker));
    }

    private clearMouseAndTouchListenersLive(marker: google.maps.Marker) {
        google.maps.event.clearListeners(marker, 'mouseover');
        google.maps.event.clearListeners(marker, 'mouseout');
        google.maps.event.clearListeners(marker, 'mousedown');
        google.maps.event.clearListeners(marker, 'mouseup');
    }

    private setMarkerLabel(marker: google.maps.Marker, fontSize: string, label = undefined) {
        if (label) {
            marker.setLabel({
                color: 'white',
                fontSize: fontSize,
                fontFamily: 'Lato',
                fontWeight: 'normal',
                text: label
            });
        } else {
            marker.setLabel(undefined);
        }
    }

    private createNativeMarker<T>(layer: LayerIndex, marker: MarkerModel<T>, z = 0): google.maps.Marker { // TODO: eliminate z parameter after payload.trip dependency is removed
        const coords = new google.maps.LatLng(marker.lat, marker.lng);
        const nativeMarker = new google.maps.Marker({
            position: coords,
            visible: false,
            zIndex: layer,
            icon: this.getMarkerIcon(marker),
            opacity: marker.opacity,
            map: this._googleMap
        });

        nativeMarker.set('attachment', marker); // attach marker model
        nativeMarker.addListener('click', () => this.markerClick(nativeMarker));
        nativeMarker.addListener('mouseover', () => this._zone.run(() => this._onEnter.next(nativeMarker)));
        nativeMarker.addListener('mouseout', () => this._zone.run(() => this._onLeave.next(nativeMarker)));

        //
        // TODO: remove app dependency on payload.trip: the trip and vehicleName "label" can become a base property
        //
        if (layer === LayerIndex.Trip && marker.payload) {
            const trip = (<any>marker.payload).trip;
            if (trip && trip.tripNumber) {
                nativeMarker.setZIndex(layer + z);
                nativeMarker.setLabel({
                    color: 'black',
                    fontSize: '11.2px',
                    fontFamily: 'Lato',
                    fontWeight: 'bold',
                    text: trip.tripNumber
                });
            }
        }

        return nativeMarker;
    }

    //
    // Imperative methods for dynamic stream support:
    //

    // appendTrace<T>(layerIndex: number, trace: TraceModel<T>) { // add an empty trace with a zero-length polyline
    //     this.validateReservedIndex(layerIndex);
    //     const layer = this._traces.get(layerIndex);
    //     const polyline = this.createNativePolyline(layerIndex, [], trace.color, trace.opacity, trace.weight);
    //     polyline.setVisible(true);
    //     layer.nativeTraces.push(polyline);
    //     layer.traces.push(trace);
    // }

    // extendTrace<T>(layerIndex: number, marker: MarkerModel<T>) { // now extend polyline out to marker coords
    //     this.validateReservedIndex(layerIndex);
    //     const layer = this._traces.get(layerIndex);
    //     const index = layer.nativeTraces.length - 1;
    //     const trace = layer.traces[index];
    //     trace.markers.push(marker);
    //     layer.nativeTraces[index].getPath().push(new google.maps.LatLng(marker.lat, marker.lng));
    // }

    // getNativeTraces(layerIndex: number): google.maps.Polyline[] {
    //     const layer = this._traces.get(layerIndex);
    //     return layer.nativeTraces;
    // }

    appendMarker<T>(layerIndex: number, marker: MarkerModel<T>): google.maps.Marker {
        this.validateReservedIndex(layerIndex);
        const layer = this._markers.get(layerIndex);
        const index = layer.nativeMarkers.length - 1;
        const nativeMarker = this.createNativeMarker(layerIndex, marker);
        nativeMarker.setVisible(true);
        layer.nativeMarkers.push(nativeMarker);
        layer.markers.push(marker);
        return nativeMarker;
    }

    private validateReservedIndex(layer: number) {
        if (layer < LayerIndex.User) {
            throw 'Dynamic trace layer index must be >= LayerIndex.Reserved';
        }
    }

    clusterize(nativeMarkers: google.maps.Marker[], googleMap: google.maps.Map, layer: LayerIndex) {
        this.clearClusterMarkers(layer);
        if (!this._markerCluster.get(layer)) {
            const clusterer = new MarkerClusterer(googleMap, nativeMarkers, {
                zoomOnClick: false,
                minimumClusterSize: MIN_CLUSTER_MARKERS,
                maxZoom: MAX_CLUSTER_ZOOM_LEVEL,
                styles: [
                    { height: 39, width: 39, textColor: 'white', url: FleetTrackerIcons.cluster }
                ]
            });
            google.maps.event.addListener(clusterer, 'clusterclick', cluster => {
                const bounds = new google.maps.LatLngBounds();
                cluster.getMarkers().forEach(bound => bounds.extend(bound.getPosition()));

                this.googleMap.fitBounds(bounds, this._padding);
            });
            this._markerCluster.set(layer, clusterer);
        } else {
            this._markerCluster.get(layer).addMarkers(nativeMarkers, false);
            this._markerCluster.get(layer).setMap(googleMap);
            this._markerCluster.get(layer).repaint();
        }
    }

    private processMarkerFontSize<T>(markers: DirectionalMarkerModel<T>[]) {
        markers.forEach(function (element, index) {
            if (element.fontSize === undefined) {
                element.fontSize = MARKER_LABEL_DEFAULT_FONT_SIZE;
            }
        });
    }

    // Initialize a cluster instance with the given set of native markers.
    //
    // Markers will not be clustered until there is a minimum of "min" markers in the group.

    removeClustering(layer: LayerIndex) {
        if (this._markerCluster.get(layer)) {
            this._markerCluster.get(layer).setMap(undefined);
        }
    }

    clearClusterMarkers(layer: LayerIndex) {
        if (this._markerCluster.get(layer)) {
            this._markerCluster.get(layer).clearMarkers();
            this._markerCluster.get(layer).repaint();
        }
    }

    // Set padding
    setPadding(padding: google.maps.Padding) {
        this._padding = padding;
    }

    // https://developers.google.com/maps/documentation/javascript/examples/event-poi?hl=en
    private static isIconMouseEvent(e: google.maps.MapMouseEvent | google.maps.IconMouseEvent): e is google.maps.IconMouseEvent {
        return 'placeId' in e;
    }
}
